import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Cta } from "@/components/site/ui";
import { submitLead, WHATSAPP_NUMBER } from "@/lib/api";
import { SERVICES } from "@/data/content";
import { CheckCircle2, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";

const SERVICE_OPTIONS = [
  ...SERVICES.map((s) => s.title),
  "Architectural Design",
  "Smart Home Solutions",
  "Full Home Interior",
  "Not sure yet",
];

export const LeadForm = ({ source = "website", ctaLabel = "Submit Request", dark = false, defaults = {} }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    service: defaults.service || "",
    message: defaults.message || "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const buildWhatsAppUrl = (data) => {
    const src = data.source || source;
    const label = src === "consultation" ? "Free Consultation" : src === "quote" ? "Instant Quote" : "General enquiry";
    const lines = [
      `Hi Spherio Living — I just submitted a ${label} request on your website.`,
      "",
      `Name: ${data.name}`,
      `Phone: ${data.phone}`,
      `Email: ${data.email}`,
      data.city ? `City: ${data.city}` : null,
      data.service ? `Service: ${data.service}` : null,
      data.message ? `Details: ${data.message}` : null,
      "",
      "Sharing on WhatsApp so we can pick it up faster.",
    ].filter(Boolean).join("\n");
    return `https://wa.me/91${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines)}`;
  };

  useEffect(() => {
    setForm((f) => ({
      ...f,
      service: defaults.service || f.service,
      message: defaults.message !== undefined ? defaults.message : f.message,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaults.service, defaults.message]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, source };
      await submitLead(payload);
      toast.success("Thank you! Our design team will reach out within 24 hours.");
      setSubmitted(payload);
      setForm({ name: "", email: "", phone: "", city: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setLoading(false);
    }
  };

  const labelCls = dark ? "text-white/70" : "text-[#0A0A0A]";
  const inputCls = dark
    ? "bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-white/40 h-12 rounded-xl"
    : "bg-white border-black/10 h-12 rounded-xl focus-visible:ring-[#1C1A17]";

  if (submitted) {
    const wa = buildWhatsAppUrl(submitted);
    const panelCls = dark
      ? "rounded-2xl bg-white/[0.04] border border-white/10 p-8"
      : "rounded-2xl bg-[#F5F1EA] border border-[#1C1A17]/20 p-8";
    const subCls = dark ? "text-white/60" : "text-[#5C574F]";
    return (
      <div data-testid="lead-success" className={panelCls}>
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-6 w-6 text-[#1C1A17]" />
          <div className={`font-head text-2xl font-medium ${dark ? "text-white" : "text-[#0A0A0A]"}`}>
            Request received.
          </div>
        </div>
        <p className={`mt-3 ${subCls} leading-relaxed`}>
          Thanks {submitted.name.split(" ")[0]} — a Spherio Living designer will reach out within 24 hours.
          For a faster reply, share your request on WhatsApp and we’ll pick it up right away.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <a
            data-testid="lead-whatsapp-handoff"
            href={wa}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-6 py-3 text-sm font-semibold hover:brightness-95 transition"
          >
            <MessageCircle className="h-4 w-4" /> Continue on WhatsApp
          </a>
          <button
            type="button"
            data-testid="lead-submit-another"
            onClick={() => setSubmitted(null)}
            className={`inline-flex items-center rounded-full px-6 py-3 text-sm font-semibold transition ${
              dark ? "border border-white/25 text-white hover:bg-white hover:text-[#0A0A0A]" : "border border-black/15 text-[#0A0A0A] hover:border-[#1C1A17] hover:text-[#1C1A17]"
            }`}
          >
            Submit another request
          </button>
        </div>
      </div>
    );
  }

  return (
    <form data-testid="lead-form" onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <div className="space-y-2">
        <Label className={labelCls}>Full name*</Label>
        <Input data-testid="lead-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" className={inputCls} />
      </div>
      <div className="space-y-2">
        <Label className={labelCls}>Phone*</Label>
        <Input data-testid="lead-phone" value={form.phone} onChange={set("phone")} placeholder="+91 98765 43210" className={inputCls} />
      </div>
      <div className="space-y-2">
        <Label className={labelCls}>Email*</Label>
        <Input data-testid="lead-email" type="email" value={form.email} onChange={set("email")} placeholder="jane@email.com" className={inputCls} />
      </div>
      <div className="space-y-2">
        <Label className={labelCls}>City</Label>
        <Input data-testid="lead-city" value={form.city} onChange={set("city")} placeholder="Lucknow" className={inputCls} />
      </div>
      <div className="space-y-2 sm:col-span-2">
        <Label className={labelCls}>Service of interest</Label>
        <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
          <SelectTrigger data-testid="lead-service" className={inputCls}>
            <SelectValue placeholder="Choose a service" />
          </SelectTrigger>
          <SelectContent>
            {SERVICE_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2 sm:col-span-2">
        <Label className={labelCls}>Tell us about your project</Label>
        <Textarea
          data-testid="lead-message"
          value={form.message}
          onChange={set("message")}
          placeholder="Home size, timeline, style preferences..."
          className={`${inputCls} h-28 py-3`}
        />
      </div>
      <div className="sm:col-span-2">
        <Cta type="submit" onClick={() => {}} data-testid="lead-submit" variant={dark ? "light" : "solid"} className="w-full sm:w-auto justify-center">
          {loading ? "Sending..." : ctaLabel}
        </Cta>
        <p
          data-testid="lead-legal-note"
          className={`mt-4 text-xs leading-relaxed ${dark ? "text-white/50" : "text-[#5C574F]"}`}
        >
          By submitting this form you agree to our{" "}
          <Link
            to="/terms"
            className={`underline underline-offset-4 ${dark ? "text-white/80 hover:text-white" : "text-[#1C1A17] hover:text-[#B8935A]"}`}
          >
            Terms &amp; Conditions
          </Link>{" "}
          and acknowledge our{" "}
          <Link
            to="/privacy-policy"
            className={`underline underline-offset-4 ${dark ? "text-white/80 hover:text-white" : "text-[#1C1A17] hover:text-[#B8935A]"}`}
          >
            Privacy Policy
          </Link>
          .
        </p>
      </div>
    </form>
  );
};
